<template>
  <div class="min-h-screen">
    <!-- Header -->
    <AppHeader />
    
    <!-- Page Hero -->
    <section class="bg-gradient-to-br from-primary-50 to-secondary-50 py-12 md:py-24">
      <div class="container-custom">
        <div v-if="loading" class="text-center">
          <SkeletonLoader type="title" width="400px" class="mx-auto mb-4" />
          <SkeletonLoader type="text" width="600px" class="mx-auto" />
        </div>
        <div v-else class="text-center">
          <h1 class="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            {{ heroSection?.data?.title || 'About Us' }}
          </h1>
          <p class="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto px-4 md:px-0">
            {{ heroSection?.data?.subtitle || 'Learn more about our journey, mission, and the team behind our success.' }}
          </p>
        </div>
      </div>
    </section>
     
    <!-- Company Story -->
    <section class="py-12 md:py-24 bg-white">
      <div class="container-custom">
        <div v-if="loading" class="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          <div class="space-y-4">
            <SkeletonLoader type="title" />
            <SkeletonLoader type="text" />
            <SkeletonLoader type="text" />
            <SkeletonLoader type="card" height="200px" />
          </div>
          <SkeletonLoader type="image" />
        </div>

        <div v-else class="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div class="text-center lg:text-left">
            <h2 class="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              {{ companyStorySection?.data?.title || 'Our Story' }}
            </h2>
            <p class="text-base md:text-lg text-gray-600 mb-6 leading-relaxed">
              {{ companyStorySection?.data?.description || 'Founded with a vision to transform businesses through technology, we have grown into a trusted partner for companies worldwide.' }}
            </p>
          </div>
          
          <div class="relative px-4 md:px-0">
            <div class="rounded-2xl overflow-hidden h-64 md:h-96 shadow-2xl">
              <img 
                v-if="companyStorySection?.data?.image_url" 
                :src="companyStorySection.data.image_url" 
                :alt="companyStorySection?.data?.title || 'Company Story'"
                class="w-full h-full object-cover"
              />
              <div v-else class="bg-gray-200 h-full flex items-center justify-center">
                <span class="text-gray-500">Company Story Image</span>
              </div>
            </div>
            <div class="absolute -top-4 -right-4 w-16 md:w-24 h-16 md:h-24 bg-primary-200 rounded-full opacity-20"></div>
            <div class="absolute -bottom-4 -left-4 w-12 md:w-16 h-12 md:h-16 bg-secondary-200 rounded-full opacity-20"></div>
          </div>
        </div>
      </div>
    </section>
    
    <!-- Mission, Vision & Values -->
    <section class="relative py-12 md:py-24 bg-gradient-to-br from-gray-50 via-white to-gray-50 overflow-hidden">
      <!-- Background Decorative Elements -->
      <div class="absolute inset-0 pointer-events-none">
        <div class="absolute top-20 left-10 w-72 h-72 bg-primary-200/20 rounded-full blur-3xl"></div>
        <div class="absolute bottom-20 right-10 w-96 h-96 bg-blue-200/20 rounded-full blur-3xl"></div>
        <div class="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-purple-200/10 rounded-full blur-3xl"></div>
      </div>

      <div class="container-custom relative z-10">
        <div v-if="loading" class="space-y-8">
          <div class="text-center">
            <SkeletonLoader type="title" width="400px" class="mx-auto mb-4" />
            <SkeletonLoader type="text" width="600px" class="mx-auto" />
          </div>
          <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <SkeletonLoader v-for="i in 3" :key="i" type="card" height="250px" />
          </div>
        </div>

        <div v-else>
          <!-- Section Header -->
          <div class="text-center mb-12 lg:mb-16 px-4">
            <div class="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-primary-100 to-blue-100 text-primary-700 text-xs md:text-sm font-semibold mb-6 shadow-sm">
              <span class="flex h-2 w-2 rounded-full bg-primary-600 mr-2 animate-pulse"></span>
              Our Core Principles
            </div>
            <h2 class="font-heading text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
              <span class="bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 bg-clip-text text-transparent">
                {{ missionVisionSection?.data?.title || 'Our Mission, Vision & Values' }}
              </span>
            </h2>
            <p class="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              {{ missionVisionSection?.data?.description || 'These core principles guide everything we do and shape how we serve our clients.' }}
            </p>
          </div>
          
          <!-- Cards Grid -->
          <div class="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8 px-4 md:px-0">
            <!-- Mission Card -->
            <div class="group relative">
              <!-- Gradient Border Effect -->
              <div class="absolute -inset-0.5 bg-gradient-to-r from-primary-600 to-purple-600 rounded-2xl opacity-0 group-hover:opacity-100 blur transition-all duration-500"></div>
              
              <div class="relative h-full bg-white rounded-2xl p-6 md:p-8 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 border border-gray-100">
                <!-- Icon Container with Gradient -->
                <div class="relative mb-6">
                  <div class="absolute inset-0 bg-gradient-to-r from-primary-400 to-purple-500 rounded-2xl blur-xl opacity-50 group-hover:opacity-75 transition-opacity"></div>
                  <div class="relative w-16 h-16 md:w-20 md:h-20 bg-gradient-to-br from-primary-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto transform group-hover:rotate-6 transition-transform duration-500 shadow-xl">
                    <svg class="w-8 h-8 md:w-10 md:h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
                    </svg>
                  </div>
                </div>
                
                <!-- Content -->
                <h3 class="font-heading text-xl md:text-2xl font-bold text-gray-900 mb-4 text-center">Our Mission</h3>
                <p class="text-sm md:text-base text-gray-600 text-center leading-relaxed">
                  {{ missionVisionSection?.data?.mission || 'To empower businesses with innovative technology solutions that drive growth and success.' }}
                </p>
                
                <!-- Decorative Bottom Line -->
                <div class="mt-6 h-1 w-16 bg-gradient-to-r from-primary-600 to-purple-600 rounded-full mx-auto"></div>
              </div>
            </div>
            
            <!-- Vision Card -->
            <div class="group relative">
              <!-- Gradient Border Effect -->
              <div class="absolute -inset-0.5 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-2xl opacity-0 group-hover:opacity-100 blur transition-all duration-500"></div>
              
              <div class="relative h-full bg-white rounded-2xl p-6 md:p-8 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 border border-gray-100">
                <!-- Icon Container with Gradient -->
                <div class="relative mb-6">
                  <div class="absolute inset-0 bg-gradient-to-r from-blue-400 to-cyan-500 rounded-2xl blur-xl opacity-50 group-hover:opacity-75 transition-opacity"></div>
                  <div class="relative w-16 h-16 md:w-20 md:h-20 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-2xl flex items-center justify-center mx-auto transform group-hover:rotate-6 transition-transform duration-500 shadow-xl">
                    <svg class="w-8 h-8 md:w-10 md:h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                    </svg>
                  </div>
                </div>
                
                <!-- Content -->
                <h3 class="font-heading text-xl md:text-2xl font-bold text-gray-900 mb-4 text-center">Our Vision</h3>
                <p class="text-sm md:text-base text-gray-600 text-center leading-relaxed">
                  {{ missionVisionSection?.data?.vision || 'To be the leading technology partner for businesses worldwide.' }}
                </p>
                
                <!-- Decorative Bottom Line -->
                <div class="mt-6 h-1 w-16 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-full mx-auto"></div>
              </div>
            </div>
            
            <!-- Values Card -->
            <div class="group relative">
              <!-- Gradient Border Effect -->
              <div class="absolute -inset-0.5 bg-gradient-to-r from-green-600 to-emerald-600 rounded-2xl opacity-0 group-hover:opacity-100 blur transition-all duration-500"></div>
              
              <div class="relative h-full bg-white rounded-2xl p-6 md:p-8 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 border border-gray-100">
                <!-- Icon Container with Gradient -->
                <div class="relative mb-6">
                  <div class="absolute inset-0 bg-gradient-to-r from-green-400 to-emerald-500 rounded-2xl blur-xl opacity-50 group-hover:opacity-75 transition-opacity"></div>
                  <div class="relative w-16 h-16 md:w-20 md:h-20 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center mx-auto transform group-hover:rotate-6 transition-transform duration-500 shadow-xl">
                    <svg class="w-8 h-8 md:w-10 md:h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
                    </svg>
                  </div>
                </div>
                
                <!-- Content -->
                <h3 class="font-heading text-xl md:text-2xl font-bold text-gray-900 mb-4 text-center">Our Values</h3>
                <div class="grid grid-cols-2 gap-y-3 gap-x-2">
                  <div v-for="value in values" :key="value" 
                       class="flex items-center gap-2 text-gray-600 group/item hover:text-green-600 transition-colors">
                    <div class="w-1.5 h-1.5 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 group-hover/item:scale-125 transition-transform"></div>
                    <span class="text-xs md:text-sm font-medium">{{ value }}</span>
                  </div>
                </div>
                
                <!-- Decorative Bottom Line -->
                <div class="mt-6 h-1 w-16 bg-gradient-to-r from-green-600 to-emerald-600 rounded-full mx-auto"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
    
    <!-- Team Section -->
    <section class="py-12 md:py-24 bg-white px-4 md:px-0">
      <div class="container-custom">
        <div v-if="loading" class="space-y-8">
          <div class="text-center">
            <SkeletonLoader type="title" width="300px" class="mx-auto mb-4" />
            <SkeletonLoader type="text" width="500px" class="mx-auto" />
          </div>
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <SkeletonLoader v-for="i in 4" :key="i" type="card" height="300px" />
          </div>
        </div>

        <div v-else>
          <div class="text-center mb-12 md:mb-16">
            <h2 class="font-heading text-3xl md:text-4xl font-bold text-primary-600 mb-4">
              Meet Our Team
            </h2>
            <p class="text-lg md:text-xl text-primary-400 max-w-3xl mx-auto">
              Talented professionals dedicated to your success
            </p>
          </div>
          
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            <div 
              v-for="member in teamMembers" 
              :key="member.id" 
              class="group card p-6 text-center hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2"
            >
              <!-- Profile Image -->
              <div class="relative w-32 h-32 mx-auto mb-4">
                <div class="w-full h-full rounded-full overflow-hidden border-4 border-gray-100 group-hover:border-primary-200 transition-colors">
                  <img 
                    v-if="member.image_url" 
                    :src="member.image_url" 
                    :alt="member.name"
                    class="w-full h-full object-cover"
                  />
                  <div v-else class="w-full h-full bg-gradient-to-br from-primary-100 to-secondary-100 flex items-center justify-center">
                    <span class="text-primary-600 text-3xl font-bold">{{ member.name.charAt(0) }}</span>
                  </div>
                </div>
              </div>

              <!-- Member Info -->
              <h3 class="font-semibold text-lg text-gray-900 mb-1">{{ member.name }}</h3>
              <p class="text-primary-600 text-sm font-medium mb-3">{{ member.position }}</p>
              <p class="text-gray-600 text-sm mb-4 line-clamp-3">{{ member.bio }}</p>

              <!-- Social Links -->
              <div v-if="hasSocialLinks(member)" class="flex justify-center gap-2 pt-4 border-t border-gray-100">
                <a 
                  v-if="member.social_links?.linkedin" 
                  :href="member.social_links.linkedin" 
                  target="_blank"
                  rel="noopener noreferrer"
                  class="w-8 h-8 flex items-center justify-center rounded-full bg-blue-50 text-blue-600 hover:bg-blue-600 hover:text-white transition-colors"
                  title="LinkedIn"
                >
                  <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/></svg>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Dev Profiles Section -->
    <section class="py-12 md:py-24 bg-gray-50/50 px-4 md:px-0">
      <div class="container-custom">
        <div v-if="loading" class="space-y-8">
          <div class="text-center">
            <SkeletonLoader type="title" width="300px" class="mx-auto mb-4" />
            <SkeletonLoader type="text" width="500px" class="mx-auto" />
          </div>
          <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
            <SkeletonLoader v-for="i in 3" :key="i" type="card" height="400px" />
          </div>
        </div>

        <div v-else-if="devProfiles.length > 0">
          <div class="text-center mb-12 md:mb-16">
            <h2 class="font-heading text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Expert Developers
            </h2>
            <p class="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
              Meet the technical masterminds behind our digital solutions
            </p>
          </div>

          <!-- Sort/Filter -->
          <div class="flex justify-center md:justify-end mb-8">
             <div class="inline-flex rounded-xl bg-white border border-gray-100 p-1 shadow-sm">
                <button 
                  v-for="sort in ['Newest', 'Experience']" 
                  :key="sort"
                  @click="currentSort = sort"
                  :class="[
                    'px-4 py-2 text-xs font-bold rounded-lg transition-all',
                    currentSort === sort ? 'bg-primary-600 text-white shadow-md' : 'text-gray-500 hover:text-gray-700'
                  ]"
                >
                  {{ sort }}
                </button>
             </div>
          </div>
          
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            <div 
              v-for="dev in sortedProfiles" 
              :key="dev.id" 
              class="group relative bg-white rounded-[2rem] overflow-hidden border border-gray-100 shadow-xl shadow-gray-200/50 hover:shadow-2xl transition-all duration-500 hover:-translate-y-2"
            >
              <!-- Cover Small -->
              <div class="h-32 bg-gradient-to-r from-primary-600 to-purple-600 relative overflow-hidden">
                <img v-if="dev.cover_image" :src="devProfilesStore.getImageUrl(dev.cover_image)" class="w-full h-full object-cover opacity-40 group-hover:scale-110 transition-transform duration-700" />
                <div class="absolute inset-0 bg-black/10"></div>
              </div>
              
              <!-- Content -->
              <div class="px-6 md:px-8 pb-8 flex flex-col items-center -mt-12 relative z-10">
                <div class="w-24 h-24 rounded-3xl overflow-hidden border-4 border-white shadow-xl bg-white mb-4">
                  <img v-if="dev.profile_image" :src="devProfilesStore.getImageUrl(dev.profile_image)" class="w-full h-full object-cover" />
                  <div v-else class="w-full h-full flex items-center justify-center text-primary-600 text-2xl font-bold">{{ dev.name.charAt(0) }}</div>
                </div>
                
                <h3 class="font-bold text-lg md:text-xl text-gray-900 text-center mb-1">{{ dev.name }}</h3>
                <p class="text-primary-600 text-xs md:text-sm font-semibold mb-6 text-center">{{ dev.title }}</p>
                
                <div class="flex flex-wrap justify-center gap-1.5 mb-6">
                   <span v-for="skill in dev.skills?.slice(0, 4)" :key="skill" class="px-2 py-0.5 bg-gray-50 text-gray-500 text-[9px] md:text-[10px] font-bold rounded border border-gray-100 uppercase tracking-tighter">
                      {{ skill }}
                   </span>
                </div>
                
                <div class="grid grid-cols-2 w-full gap-4 mb-8 py-4 border-t border-b border-gray-50">
                   <div class="text-center border-r border-gray-50">
                      <p class="text-base md:text-lg font-black text-gray-900">{{ dev.experience_years }}+</p>
                      <p class="text-[8px] md:text-[9px] font-bold text-gray-400 uppercase tracking-widest">Years Exp</p>
                   </div>
                   <div class="text-center">
                      <p class="text-base md:text-lg font-black text-gray-900">{{ dev.projects_completed }}+</p>
                      <p class="text-[8px] md:text-[9px] font-bold text-gray-400 uppercase tracking-widest">Projects</p>
                   </div>
                </div>
                
                <router-link 
                  :to="'/profile/' + dev.slug"
                  class="w-full py-3 bg-gray-900 text-white text-xs font-black rounded-xl text-center hover:bg-primary-600 transition-all shadow-lg shadow-gray-200"
                >
                  VIEW PROFILE
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
    
    <!-- Stats Section -->
    <section class="relative overflow-hidden bg-gray-50 py-12 md:py-24 px-4 md:px-0">
      <div class="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div class="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-full bg-primary-200/40 blur-3xl"></div>
        <div class="absolute -bottom-[20%] -left-[10%] w-[40%] h-[40%] rounded-full bg-secondary-200/40 blur-3xl"></div>
        <div class="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[30%] h-[30%] rounded-full bg-purple-200/30 blur-3xl"></div>
      </div>

      <div class="container-custom relative z-10">
        <div v-if="loading" class="space-y-8">
          <div class="text-center">
            <SkeletonLoader type="title" width="400px" class="mx-auto mb-4" />
            <SkeletonLoader type="text" width="600px" class="mx-auto" />
          </div>
          <div class="grid grid-cols-2 lg:grid-cols-4 gap-8">
            <SkeletonLoader v-for="i in 4" :key="i" type="stat" />
          </div>
        </div>

        <div v-else>
          <div class="text-center mb-12 lg:mb-16">
            <div class="inline-flex items-center px-4 py-2 rounded-full bg-white/80 backdrop-blur-sm border border-primary-100 text-primary-600 text-xs md:text-sm font-medium mb-6 shadow-lg">
              <span class="flex h-2 w-2 rounded-full bg-primary-600 mr-2 animate-pulse"></span>
              Our Track Record
            </div>
            <h2 class="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Our Achievements
            </h2>
            <p class="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
              Numbers that speak for our commitment to excellence
            </p>
          </div>
          
          <div class="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-8">
            <div v-for="(stat, index) in statistics" :key="index" class="text-center bg-white/60 backdrop-blur-sm rounded-2xl p-6 md:p-8 border border-gray-200/50 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105">
              <div class="text-3xl md:text-5xl font-bold bg-gradient-to-r from-primary-600 to-purple-600 bg-clip-text text-transparent mb-2">{{ stat.value }}</div>
              <div class="text-xs md:text-lg text-gray-700 font-medium">{{ stat.label }}</div>
            </div>
          </div>
        </div>
      </div>
    </section>
    
    <!-- Footer -->
    <AppFooter />
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useContentStore } from '@/stores/content'
import { useTeamMembersStore } from '@/stores/teamMembers'
import { useStatisticsStore } from '@/stores/statistics'
import { useDevProfilesStore } from '@/stores/devProfiles'
import AppHeader from '@/components/layout/AppHeader.vue'
import AppFooter from '@/components/layout/AppFooter.vue'
import SkeletonLoader from '@/components/SkeletonLoader.vue'

const contentStore = useContentStore()
const teamMembersStore = useTeamMembersStore()
const statisticsStore = useStatisticsStore()
const devProfilesStore = useDevProfilesStore()

const loading = ref(true)
const currentSort = ref('Newest')

onMounted(async () => {
  loading.value = true
  try {
    await Promise.all([
      contentStore.fetchPageSections('about'),
      teamMembersStore.fetchTeamMembers(),
      statisticsStore.fetchStatistics(),
      devProfilesStore.fetchDevProfiles()
    ])
  } catch (error) {
    console.error('Error loading about page content:', error)
  } finally {
    loading.value = false
  }
})

const aboutSections = computed(() => contentStore.pageSections.about || [])
const heroSection = computed(() => aboutSections.value.find(s => s.section === 'hero'))
const companyStorySection = computed(() => aboutSections.value.find(s => s.section === 'company_story'))
const missionVisionSection = computed(() => aboutSections.value.find(s => s.section === 'mission_vision_values'))

const teamMembers = computed(() => teamMembersStore.teamMembers || [])
const statistics = computed(() => statisticsStore.statistics || [])
const devProfiles = computed(() => devProfilesStore.devProfiles || [])

const sortedProfiles = computed(() => {
  const profiles = devProfiles.value.filter(dev => dev.is_active === true)
  if (currentSort.value === 'Experience') {
    return profiles.sort((a, b) => b.experience_years - a.experience_years)
  }
  return profiles.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
})

const values = computed(() => {
  const vals = missionVisionSection.value?.data?.values
  if (vals && Array.isArray(vals)) return vals
  return ['Innovation', 'Quality', 'Integrity', 'Customer Focus']
})

const hasSocialLinks = (member) => {
  if (!member.social_links || typeof member.social_links !== 'object') return false
  return Object.values(member.social_links).some(link => link && link.trim() !== '')
}
</script>

<style scoped>
.container-custom {
  max-width: 80rem;
  margin-left: auto;
  margin-right: auto;
  padding-left: 1rem;
  padding-right: 1rem;
}

.section-padding {
  padding-top: 5rem;
  padding-bottom: 5rem;
}

.font-heading {
  font-family: 'Inter', sans-serif;
}
</style>
